import { useEffect, useState } from "react";
import { Loader2, RefreshCw, StickyNote, Lock, Car, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("uk-UA").format(Math.round(n)));

const STATUSES = [
  { v: "new", l: "Новий", c: "#22c55e" },
  { v: "contacted", l: "Контакт", c: "#f59e0b" },
  { v: "importing", l: "Довіз", c: "#3b82f6" },
  { v: "closed", l: "Закрито", c: "#71717a" },
];
const statusMeta = (v) => STATUSES.find((s) => s.v === v) || STATUSES[0];

export default function AdminOrders() {
  const [key, setKey] = useState(localStorage.getItem("adt_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  async function load(k = key) {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/orders`, { headers: { "X-Admin-Key": k } });
      if (r.status === 401) { setErr("Невірний ключ доступу"); setAuthed(false); setLoading(false); return; }
      const d = await r.json();
      setOrders(Array.isArray(d) ? d : []);
      setAuthed(true);
      localStorage.setItem("adt_admin_key", k);
    } catch { setErr("Помилка з'єднання"); }
    setLoading(false);
  }
  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function patch(id, body) {
    const r = await fetch(`${API}/orders/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", "X-Admin-Key": key },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      const updated = await r.json();
      setOrders((o) => o.map((x) => (x.id === id ? updated : x)));
      if (sel?.id === id) setSel(updated);
    }
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070707] p-6 font-body text-white">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8" data-testid="admin-login">
          <div className="mb-4 flex items-center gap-2 text-[#C73D3A]"><Lock size={18} /><span className="font-heading text-lg font-bold">ADT · Адмін</span></div>
          <input data-testid="admin-key-input" type="password" placeholder="Ключ доступу"
            className="w-full rounded-lg border border-white/12 bg-white/5 px-3 py-2.5 text-white outline-none focus:border-[#C73D3A]"
            value={key} onChange={(e) => setKey(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
          {err && <p className="mt-2 text-sm text-[#C73D3A]" data-testid="admin-error">{err}</p>}
          <button data-testid="admin-login-btn" onClick={() => load()}
            className="mt-4 w-full rounded-lg bg-[#C73D3A] py-2.5 font-semibold text-white hover:bg-[#d84a47]">Увійти</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070707] font-body text-white">
      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-heading text-3xl font-bold">Заявки на довіз <span className="text-white/40">({orders.length})</span></h1>
          <button data-testid="admin-refresh" onClick={() => load()} className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:border-[#C73D3A]">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Оновити
          </button>
        </div>

        {orders.length === 0 && <p className="text-white/40">Поки що немає заявок.</p>}

        <div className="grid gap-3" data-testid="admin-orders">
          {orders.map((o) => {
            const m = statusMeta(o.status);
            return (
              <div key={o.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-heading font-bold">
                      <Car size={16} className="text-[#C73D3A]" />
                      {o.vehicle?.make} {o.vehicle?.model} {o.vehicle?.year}
                      <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: `${m.c}22`, color: m.c }}>{m.l}</span>
                    </div>
                    <div className="mt-1 text-sm text-white/70">{o.customer_name} · {o.contact}{o.email ? ` · ${o.email}` : ""}{o.messenger ? ` · ${o.messenger}` : ""}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-white/45">
                      <span>Під ключ: <b className="text-white/80">{fmt(o.final_price_uah)} ₴</b></span>
                      <span>Sofi Score: {o.sofi_score ?? "—"}</span>
                      <span className="uppercase">{o.vehicle?.source || "—"}</span>
                      {o.vehicle?.url && <a href={o.vehicle.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white"><ExternalLink size={12} />оголошення</a>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <select data-testid={`admin-status-${o.id}`} value={o.status} onChange={(e) => patch(o.id, { status: e.target.value })}
                      className="rounded-lg border border-white/12 bg-[#111] px-3 py-1.5 text-sm outline-none">
                      {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                    <button data-testid={`admin-notes-${o.id}`} onClick={() => setSel(sel?.id === o.id ? null : o)}
                      className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><StickyNote size={13} /> Нотатки ({o.notes?.length || 0})</button>
                  </div>
                </div>

                {sel?.id === o.id && (
                  <div className="mt-3 border-t border-white/10 pt-3" data-testid="admin-notes-panel">
                    <div className="space-y-1.5">
                      {(o.notes || []).map((n, i) => (
                        <div key={i} className="text-sm text-white/70"><span className="text-white/30">{new Date(n.at).toLocaleString("uk-UA")}:</span> {n.text}</div>
                      ))}
                      {(o.notes || []).length === 0 && <div className="text-sm text-white/30">Немає нотаток.</div>}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <input data-testid="admin-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Додати нотатку про комунікацію…"
                        className="flex-1 rounded-lg border border-white/12 bg-white/5 px-3 py-2 text-sm outline-none focus:border-[#C73D3A]"
                        onKeyDown={(e) => { if (e.key === "Enter" && note.trim()) { patch(o.id, { note }); setNote(""); } }} />
                      <button data-testid="admin-note-add" onClick={() => { if (note.trim()) { patch(o.id, { note }); setNote(""); } }}
                        className="rounded-lg bg-[#C73D3A] px-4 text-sm font-semibold hover:bg-[#d84a47]">Додати</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
